<template>
  <AppSection id="scopes" :title="$getConst.pages.pageUsers.users" type="page">
    <AppSection
      id="list-users"
      type="section"
      :button="hasPermission($getConst.permissions.addUser)"
      :title="$getConst.pages.pageUsers.list_of_users"
      :button-text="$getConst.pages.button.add"
      :button-icon="$getConst.icons.add"
      @onClick="openUserAddModal"
      second-button
    >
      <template #secondButton>
        <b-button @click="exportExcel" variant="link" class="ml-auto text-success font-weight-bold">
          <AppSvgIcon class="mr-2" :icon="$getConst.svgIcons.excel"/>{{$t($getConst.pages.excel.excel) | capitalize}}
        </b-button>
      </template>

      <AppDataTable
        ref="usersTable"
        :fields="fields"
        :actions="actions"
        end-point="users"
        @onEdit="edit"
        @onLogAs="logAs"
      >

        <template v-slot:id="{data,row}">
          <b-link @click="goToUserDetails(row.id)">{{row.name}}<i class="fa fa-external-link ml-2"></i></b-link>
        </template>

        <template v-slot:isSignUpCompleted="item">
          <b-badge v-if="item.data.toLowerCase() === 'completed'" class="gradient" pill variant="secondary">
            {{$t($getConst.pages.pageUsers.signup_completed)}}
          </b-badge>
          <b-badge v-else-if="item.data.toLowerCase() === 'waiting'" class="gradient" pill variant="warning">
            {{$t($getConst.pages.pageUsers.invitation_sent)}}
          </b-badge>
          <b-badge v-else-if="item.data.toLowerCase() === 'expired'" class="gradient" pill variant="danger">
            {{$t($getConst.pages.pageUsers.invitation_expired)}}
          </b-badge>
        </template>

        <template #button-onLogAs="{action, user}">
          <b-button
            v-if="hasAnyOfPermissions(action.permissions)"
            :variant="action.variant"
            class="ml-2 border-0"
            size="sm"
            :disabled="!isInSameScope(user) || isAdmin(user.role.name)"
            @click="logAs(user)">
            <i :class="action.icon"></i>
          </b-button>
        </template>

        <template #button-onResendInvitation="{action, user}">
          <b-button
            v-if="hasAnyOfPermissions(action.permissions)"
            :variant="action.variant"
            class="ml-2 border-0"
            size="sm"
            :disabled="user.isSignUpCompleted.toLowerCase() !== 'expired'|| isResendingInvitation"
            @click="resendInvitation(user.id)">
            <i :class="action.icon"></i>
          </b-button>
        </template>

      </AppDataTable>
    <UserModal :id="userAddModal"/>
    <UserModal :id="userAssignModal" :user-to-edit="userToEdit"/>
    </AppSection>
  </AppSection>
</template>
<script>
  import AppSection from "../../components/common/sections/AppSection";
  import AppDataTable from "../../components/common/table/AppDataTable";
  import UserModal from "./UserModal";
  import {exportUsers, logAs, resendSignUpInvitationEmail} from "../../api/user.api";
  import {snotifyConfig} from "../../shared/constants/snotify";
  import {getAllRoles} from "../../api/role.api";


  export default {
    name: "index",
    components: {AppSection, AppDataTable, UserModal},
    data() {
      return {
        endPoint: null,
        users: [],
        roles: [],
        userToEdit: null,
        userAddModal: 'user-add-modal',
        userAssignModal: 'user-assign-modal',
        isResendingInvitation: false,
        fields: [
          {key: 'id', label:'ID', sortable: true},
          {key: 'name', label:'Name', sortable: true, filterable: true, sortKey: 'firstName'},
          {key: 'role.name', label: 'role', sortable: false, filterable: true},
          {key: 'scopes.name', label: 'Scopes', sortable: false, filterable: true},
          {key: 'isSignUpCompleted', label: 'status', filterable: true},
        ],
        actions: [
          {action: 'onEdit', variant: 'outline-secondary', icon: this.$getConst.icons.edit, permissions: [this.$getConst.permissions.updateUser]},
          {action: 'onLogAs', variant: 'outline-warning', icon: this.$getConst.icons.eye, permissions: [this.$getConst.permissions.impersonateUser]},
          {action: 'onResendInvitation', variant: 'outline-success', icon: this.$getConst.icons.paper_plan, permissions: [this.$getConst.permissions.impersonateUser]},
        ],
      }
    },
    created() {
      this.getRoles();

      this.updateStatusField();
    },
    mounted() {
      if(this.$refs.usersTable) {
        this.$root.$on('onEditUser', user => {
          this.onAssignUserToleScope(user);
        });
        this.$root.$on('onAddUser', user => {
          this.onAddUser(user);
        });
      }
    },
    methods: {
      edit(user) {
        this.userToEdit = user;
        this.openAssignUserModal();
      },
      resendInvitation(userId) {
        this.isResendingInvitation = true;
        this.$snotify.async(
          this.$t(this.$getConst.pages.pageUsers.resending_invitation),
          this.$t(this.$getConst.pages.alerts.please_wait),
          () => new Promise((resolve, reject) => {
            resendSignUpInvitationEmail(userId).then(
              (response) => {
                this.$refs.usersTable.editItem(response.data);
                this.isResendingInvitation = false;
                resolve({
                  title: this.$t(this.$getConst.pages.alerts.success),
                  body: this.$t(this.$getConst.pages.alerts.success_operation),
                  config: snotifyConfig,
                })
              },
              (error) => {
                this.isResendingInvitation = false;
                const errorBody = this.resolveErrorMessage(error);
                reject({
                  title: errorBody.code,
                  body: errorBody.message,
                  config: snotifyConfig
                });
              }
            )
          })
        )
      },
      openUserAddModal() {
        this.$bvModal.show(this.userAddModal);
      },
      openAssignUserModal() {
        this.$bvModal.show(this.userAssignModal);
      },
      onAssignUserToleScope(user) {
        this.$snotify.success(this.$t(this.$getConst.pages.pageUsers.user_updated));
        this.$refs.usersTable.editItem(user);
      },
      onAddUser(user) {
        this.$snotify.success(this.$t(this.$getConst.pages.pageUsers.user_added));
        this.$refs.usersTable.addItem(user);
      },
      logAs(user) {
         if (this.usersHasSameScopeAsOrigin(user)) {
          logAs(user.id).then(
            response => {
              sessionStorage.setItem("loggedAs", JSON.stringify(response.data));
              this.$store.dispatch("auth/logAs", response.data);
              this.$router.push("/timesheets")
              this.$store.dispatch("loader/hideLoadApp");
            },
            error => {
              this.$store.dispatch("loader/hideLoadApp");
              this.handleApiError(error);
            }
          )
        } else {
           let redirectUri = user.scopes[0].url + "/timesheets#loggedAs=" + user.id;

           let toastClosed = false;

           this.$snotify.confirm(this.$t(this.$getConst.pages.alerts.redirecting_to_other_scope) + user.scopes[0].url, {
             timeout: 5000,
             showProgressBar: true,
             closeOnClick: false,
             pauseOnHover: true,
             buttons: [
               {text: 'Yes', action: () => location.replace(redirectUri)},
               {text: 'No', action: (toast) => {
                   this.$snotify.remove(toast.id);
                   toastClosed = true;
                 }}
             ]
           }).on("destroyed", (toast => {
             if (!toastClosed) {
               location.replace(redirectUri)
             }
           }));
        }
      },
      isInSameScope(user) {
        const scopesUserConnected = this.$store.getters["auth/allScopes"];
        let isFound = false;
        scopesUserConnected.forEach(scope => {
          if (user.scopes.some(element => element.id === scope.id)) {
            isFound = true;
            return true;
          }
        });

        return isFound;
      },
      getRoles() {
        getAllRoles().then(listOfAllRoles => {
          this.roles = listOfAllRoles.data.data;
          this.updateRoleField(this.roles);
        })
      },
      updateRoleField(roles) {
        this.fields[2].filterable = roles.map(role => {
          return {value: role.name, text: role.name};
        });
      },

       updateStatusField() {
        this.fields[4].filterable = [
          {
            value: 'completed', text: this.$t(this.$getConst.pages.pageUsers.signup_completed)
          },
          {
            value: 'expired', text: this.$t(this.$getConst.pages.pageUsers.invitation_expired)
          },
          {
            value: 'waiting', text: this.$t(this.$getConst.pages.pageUsers.invitation_sent)
          },
        ];
      },

      exportExcel() {
        exportUsers({
            responseType: 'blob',
            headers: {
              'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            }
          }).then((response) => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            const today = new Date();
            const date = today.getFullYear() + '-' + (today.getMonth() + 1) + '-' + today.getDate();
            const time = today.getHours() + ":" + today.getMinutes() + ":" + today.getSeconds();
            const dateTime = date + ' ' + time;
            link.setAttribute('download', 'users-' + '-' + dateTime + '.xlsx')
            document.body.appendChild(link);
            link.click();
            window.URL.revokeObjectURL(url);
          },
          (error) => {
            this.handleApiError(error);
          })
      },

      usersHasSameScopeAsOrigin(user) {
        return user.scopes.find(scope => scope.url === window.location.origin);
      },

      goToUserDetails(userId) {
        this.$router.push({name: this.$getConst.routesNames.administration.userDetails, params: {user_id: userId}});
      }

    },

    beforeDestroy() {
      this.$root.$off('onAddUser');
      this.$root.$off('onEditUser');
    }
  }
</script>
